import React, { useEffect, useRef, useState, useCallback } from "react";
import { Mic, MicOff, Stethoscope } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "../lib/notify";
import { t, useLang } from "../lib/i18n";
import { api } from "../lib/api";

/**
 * VoiceChat — peer-to-peer WebRTC voice chat (mesh) for 2-4 player rooms.
 * Signaling travels on the game WebSocket (`voice_*` messages, relayed by the
 * server to `target_user_id`).
 *
 * HOW A PAIR OF PLAYERS GETS CONNECTED
 *
 * One side of every pair is the CALLER — the player with the smaller user_id —
 * and it alone creates offers. The other side, the CALLEE, only answers; when
 * it wants a link (its mic just went on) it asks with `voice_hello`. Offers
 * can therefore never cross, so there is nothing to roll back.
 *
 * Every connection the caller builds gets a fresh call id (`cid`), carried by
 * all its messages. A message for another cid belongs to a connection that no
 * longer exists and is dropped; an offer with a new cid replaces the callee's
 * connection. That is also how a page reload on either side heals.
 *
 * Signaling can lose messages (WebSocket reconnecting, other player offline):
 *  - what is sent while our WebSocket is down waits in an outbox;
 *  - a watchdog (`tick`, every second) restarts ICE on a link that dropped and
 *    rebuilds a link that is still not connected after its deadline.
 * A link that cannot be established is reported with its cause (see
 * VOICE_FAIL_KEY) while the watchdog keeps retrying in the background.
 *
 * Audio: one sendrecv audio m-line per pair. The mic is attached with
 * `replaceTrack`, so switching it on or off never renegotiates. Playback goes
 * through an <audio> element — NOT through the AudioContext — because the
 * browser's echo canceller takes its reference from media elements.
 */

// ---------------------------------------------------------------------------
// ICE config
// ---------------------------------------------------------------------------
// STUN only finds a direct path. When the players' networks do not let them
// reach each other (mobile data, Wi-Fi isolating its devices, VPN, firewall)
// the voice needs a TURN relay. The list normally comes from the backend
// (GET /voice/ice-servers, configured there with TURN_URLS …: see
// backend/voice_ice.py); this one is only the fallback until it has answered,
// or if it cannot be reached. REACT_APP_TURN_* can still add a relay at build
// time. No relay is bundled — the public OpenRelay server is out of service.
const ICE_SERVERS = (() => {
  const list = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun.cloudflare.com:3478" },
  ];
  const turnUrl  = process.env.REACT_APP_TURN_URL;
  const turnUser = process.env.REACT_APP_TURN_USERNAME;
  const turnCred = process.env.REACT_APP_TURN_CREDENTIAL;
  if (turnUrl && turnUser && turnCred) {
    const urls = turnUrl.split(",").map((u) => u.trim()).filter(Boolean);
    list.push({ urls, username: turnUser, credential: turnCred });
  }
  return list;
})();

// ---------------------------------------------------------------------------
// Timings (ms)
// ---------------------------------------------------------------------------
const TICK_MS          = 1000;
const LINK_PATIENCE    = 15000; // a link not connected after this is rebuilt…
const PATIENCE_STEP    = 5000;  // …a little later after each failed attempt…
const PATIENCE_MAX     = 40000; // …up to this
const FRESH_MS         = 4000;  // a `hello` that crossed our own new offer is ignored
const DISCONNECT_GRACE = 4000;  // "disconnected" often heals by itself
const ICE_RESTART_EVERY = 5000;
const HELLO_EVERY      = 5000;
const RESYNC_SOON      = 4000;  // after the WebSocket or a player comes back
const OUTBOX_TTL       = 20000;
const OUTBOX_MAX       = 80;
const SPEAK_POLL_MS    = 100;
const SPEAK_HOLD_MS    = 350;   // keeps the indicator steady between syllables
const SPEAK_LEVEL      = 18;
const OUTDATED_TTL     = 30000; // how long a message from an old client counts
const LISTEN_KEY       = "rami_voice_listen";

// Why there is no voice link with a player → message shown to the user.
//  network  — both sides talked, but no network path works between them
//             (strict NATs: only a TURN relay can connect them)
//  silent   — the other player's app never answered
//  outdated — the other player runs a version from before this protocol
export const VOICE_FAIL_KEY = {
  network:  "voice.failNetwork",
  silent:   "voice.failSilent",
  outdated: "voice.failOutdated",
};
const hasTurn = (servers) => servers.some((s) => [].concat(s.urls).some((u) => /^turns?:/.test(u)));
const ICE_REFRESH_MS = 30 * 60 * 1000;

// ---------------------------------------------------------------------------
// Helpers (module-level, no hooks)
// ---------------------------------------------------------------------------

/** Whether the player chose to hear the others (remembered across games). */
export function loadVoiceListen() {
  try { return localStorage.getItem(LISTEN_KEY) === "1"; } catch { return false; }
}
export function saveVoiceListen(on) {
  try { localStorage.setItem(LISTEN_KEY, on ? "1" : "0"); } catch { /* ignore */ }
}

/**
 * Request mic stream with progressive constraint fallback for maximum
 * cross-browser/device compatibility.
 *
 * Permission errors (NotAllowedError, NotFoundError) are re-thrown immediately
 * because no constraint relaxation would help.
 */
async function requestMicStream() {
  const attempts = [
    // channelCount: 1 (mono) significantly improves browser AEC accuracy
    { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: { ideal: 1 } },
    { echoCancellation: true, noiseSuppression: true, channelCount: { ideal: 1 } },
    { echoCancellation: true, noiseSuppression: true },
    true,
  ];
  let lastErr;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: constraints, video: false });
    } catch (e) {
      lastErr = e;
      const name = e?.name || "";
      // Unrecoverable: re-throw immediately.
      if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "NotFoundError") throw e;
    }
  }
  throw lastErr;
}

/**
 * Prefer Opus codec on an RtpTransceiver using setCodecPreferences.
 * Supported: Chrome 96+, Safari 15.4+, Firefox 113+.
 * Silently ignored on older browsers.
 */
function preferOpus(transceiver) {
  try {
    if (typeof RTCRtpSender?.getCapabilities !== "function") return;
    const caps = RTCRtpSender.getCapabilities("audio");
    if (!caps?.codecs?.length) return;
    // Force mono Opus (stereo=0) so the codec never produces a stereo path that
    // bypasses the browser's AEC reference on the receiver side.
    const opus = caps.codecs
      .filter((c) => c.mimeType?.toLowerCase() === "audio/opus")
      .map((c) => {
        const base = (c.sdpFmtpLine || "")
          .replace(/;?\bstereo=\d\b/g, "")
          .replace(/;?\bsprop-stereo=\d\b/g, "");
        return { ...c, sdpFmtpLine: base ? `${base};stereo=0;sprop-stereo=0` : "stereo=0;sprop-stereo=0" };
      });
    const rest = caps.codecs.filter((c) => c.mimeType?.toLowerCase() !== "audio/opus");
    if (opus.length && typeof transceiver.setCodecPreferences === "function") {
      transceiver.setCodecPreferences([...opus, ...rest]);
    }
  } catch {
    /* not supported on this browser — ignore */
  }
}

const newCallId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const isLinked = (pc) => pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed";

const trackOf = (streamRef) => streamRef.current?.getAudioTracks()[0] || null;

// "direct" or "relay": the kind of path a connected link ended up on.
async function routeOf(pc) {
  try {
    const stats = await pc.getStats();
    let pair = null;
    stats.forEach((s) => {
      if (s.type === "transport" && s.selectedCandidatePairId) pair = stats.get(s.selectedCandidatePairId);
    });
    const local = pair && stats.get(pair.localCandidateId);
    if (!local) return "unknown";
    return local.candidateType === "relay" ? "relay" : "direct";
  } catch {
    return "unknown";
  }
}

// host / mdns (a host hidden behind a .local name) / srflx / relay
function kindOf(candidate) {
  const text = candidate?.candidate || "";
  const type = / typ (\w+)/.exec(text)?.[1] || "?";
  return type === "host" && /\.local /.test(text) ? "mdns" : type;
}

const tally = (counts) => Object.entries(counts).map(([k, n]) => `${k} ${n}`).join(", ") || "none";

// The candidate pairs ICE tried and how each ended, e.g. "host>host failed ×2".
async function pairSummary(pc) {
  try {
    const stats = await pc.getStats();
    const count = {};
    stats.forEach((s) => {
      if (s.type !== "candidate-pair") return;
      const local = stats.get(s.localCandidateId)?.candidateType || "?";
      const remote = stats.get(s.remoteCandidateId)?.candidateType || "?";
      const key = `${local}>${remote} ${s.state}`;
      count[key] = (count[key] || 0) + 1;
    });
    return Object.entries(count).map(([k, n]) => (n > 1 ? `${k} ×${n}` : k)).join(", ") || "none";
  } catch {
    return "n/a";
  }
}

// Traces for whoever debugs a call from the browser console.
const trace = (...args) => {
  // eslint-disable-next-line no-console
  console.info("[VoiceChat]", ...args);
};

// How long a link may stay unconnected before it is rebuilt.
const patienceAfter = (attempts) => Math.min(LINK_PATIENCE + attempts * PATIENCE_STEP, PATIENCE_MAX);

// Candidates that arrived before the remote description.
async function flushCandidates(peer) {
  const buffered = peer.iceBuffer;
  peer.iceBuffer = [];
  for (const c of buffered) {
    try { await peer.pc.addIceCandidate(c); } catch { /* ignore */ }
  }
}

const sameMap = (a, b) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VoiceChat = ({
  wsRef,
  wsVersion = 0,
  selfUserId,
  players,
  inputMuted = true,
  onSpeakingChange,
  onLinksChange,
  onMicOn,
}) => {
  useLang();
  const [micActive, setMicActive]       = useState(false);  // outgoing mic on/off
  const [micHeld, setMicHeld]           = useState(false);  // mic on but paused by the system (call, …)
  const [speaking, setSpeaking]         = useState({}); // remote user_id → bool
  const [selfSpeaking, setSelfSpeaking] = useState(false); // local mic speaking
  // remote user_id → "connecting" | "connected" | a VOICE_FAIL_KEY cause
  const [links, setLinks]               = useState({});

  const localStreamRef  = useRef(null);
  // peersRef: user_id → {
  //   pc, cid, audioEl, remoteStream, sender, analyser, source, talkUntil,
  //   iceBuffer:  candidates received before the remote description
  //   createdAt, badSince, deadline, restartAt   ← watchdog (see tick)
  // }
  const peersRef         = useRef({});
  const chainsRef        = useRef({}); // user_id → promise: signaling for one peer runs in order
  const outboxRef        = useRef([]); // signals waiting for the WebSocket
  const attemptsRef      = useRef({}); // user_id → rebuilds since the last time it was connected
  const helloAtRef       = useRef({}); // user_id → last `voice_hello` sent
  const waitingRef       = useRef({}); // user_id → since when a callee waits for an offer
  const warnedRef        = useRef({}); // user_id → cause already announced by a toast
  const whyRef           = useRef({}); // user_id → cause of the current failure, until linked
  const outdatedRef      = useRef({}); // user_id → last message received from an old client
  const diagRef          = useRef({}); // user_id → what happened to its last connections (see report)
  const iceRef           = useRef({ servers: ICE_SERVERS, turn: hasTurn(ICE_SERVERS), from: "built-in", at: 0 });
  const [report, setReport] = useState(null); // text of the diagnostic shown in the dialog
  const audioCtxRef      = useRef(null);
  const micActiveRef     = useRef(false);
  micActiveRef.current   = micActive;
  const inputMutedRef    = useRef(true);
  inputMutedRef.current  = inputMuted;

  const selfAnalyserRef = useRef(null);
  const selfSourceRef   = useRef(null);
  const selfTalkUntil   = useRef(0);

  // Bots have no WebSocket — exclude them so we never attempt a peer connection.
  const otherPlayers = (players || []).filter((p) => p.user_id !== selfUserId && !p.is_bot);
  const playersRef   = useRef(otherPlayers);
  playersRef.current = otherPlayers;

  // ---- AudioContext (speaking indicators only — never used for playback) ----
  const ensureAudioCtx = () => {
    if (audioCtxRef.current) return audioCtxRef.current;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtxRef.current = new Ctx();
    return audioCtxRef.current;
  };

  const resumeAudioCtx = useCallback(async () => {
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state === "suspended") {
      try { await ctx.resume(); } catch { /* ignore */ }
    }
  }, []);

  // IMPORTANT: the analyser is NOT routed to ctx.destination. The browser AEC
  // (Acoustic Echo Cancellation) uses HTMLMediaElement playback as its
  // reference signal; playing through the AudioContext would cause echo.
  const makeAnalyser = (stream) => {
    const ctx = ensureAudioCtx();
    if (!ctx) return null;
    try {
      const source   = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      return { source, analyser };
    } catch {
      return null;
    }
  };

  const attachAnalyzer = (peerUserId, stream) => {
    const peer = peersRef.current[peerUserId];
    if (!peer) return;
    try { peer.source?.disconnect(); } catch { /* ignore */ }
    const made = makeAnalyser(stream);
    peer.source   = made?.source || null;
    peer.analyser = made?.analyser || null;
  };

  const attachSelfAnalyser = (stream) => {
    try { selfSourceRef.current?.disconnect(); } catch { /* ignore */ }
    const made = makeAnalyser(stream);
    selfSourceRef.current   = made?.source || null;
    selfAnalyserRef.current = made?.analyser || null;
  };

  const detachSelfAnalyser = () => {
    try { selfSourceRef.current?.disconnect(); } catch { /* ignore */ }
    selfSourceRef.current   = null;
    selfAnalyserRef.current = null;
    selfTalkUntil.current   = 0;
    setSelfSpeaking(false);
  };

  // One slow poll for every analyser. A voice counts as "speaking" until it
  // has been quiet for SPEAK_HOLD_MS, so the indicator (and the board that
  // re-renders with it) does not flicker on every syllable.
  useEffect(() => {
    const data = new Uint8Array(256);
    const loud = (analyser) => {
      if (!analyser) return false;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      return sum / data.length > SPEAK_LEVEL;
    };
    const id = setInterval(() => {
      const now = Date.now();
      const next = {};
      for (const [uid, peer] of Object.entries(peersRef.current)) {
        if (loud(peer.analyser)) peer.talkUntil = now + SPEAK_HOLD_MS;
        if ((peer.talkUntil || 0) > now) next[uid] = true;
      }
      setSpeaking((prev) => (sameMap(prev, next) ? prev : next));
      if (micActiveRef.current && loud(selfAnalyserRef.current)) selfTalkUntil.current = now + SPEAK_HOLD_MS;
      setSelfSpeaking(micActiveRef.current && selfTalkUntil.current > now);
    }, SPEAK_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ---- ICE servers: asked from the backend, which knows the TURN relay ----
  const loadIce = useCallback(async () => {
    try {
      const { data } = await api.get("/voice/ice-servers");
      if (Array.isArray(data?.iceServers) && data.iceServers.length) {
        iceRef.current = { servers: data.iceServers, turn: hasTurn(data.iceServers), from: "server", at: Date.now() };
      }
    } catch {
      /* keep the list we have */
    }
  }, []);

  useEffect(() => {
    loadIce();
    const id = setInterval(loadIce, ICE_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadIce]);

  // ---- Signaling ----
  const wsOpen = useCallback(() => wsRef.current?.readyState === 1, [wsRef]);

  const sendSignal = useCallback((target_user_id, type, data = null, cid = null) => {
    const msg = { type, target_user_id, data, cid };
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      try { ws.send(JSON.stringify(msg)); return; } catch { /* fall through to the outbox */ }
    }
    const box = outboxRef.current;
    box.push({ msg, at: Date.now() });
    if (box.length > OUTBOX_MAX) box.splice(0, box.length - OUTBOX_MAX);
  }, [wsRef]);

  const flushOutbox = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    const box = outboxRef.current;
    outboxRef.current = [];
    const now = Date.now();
    for (const { msg, at } of box) {
      if (now - at > OUTBOX_TTL) continue;
      try { ws.send(JSON.stringify(msg)); } catch { /* ignore */ }
    }
  }, [wsRef]);

  // I make the offers for this pair when my user_id is the smaller one.
  const isCaller = useCallback((peerUserId) => selfUserId < peerUserId, [selfUserId]);

  // Signaling steps of one peer never overlap: each waits for the previous one.
  const enqueue = useCallback((peerUserId, step) => {
    const prev = chainsRef.current[peerUserId] || Promise.resolve();
    const next = prev.then(step).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[VoiceChat] signaling error for peer", peerUserId, err);
      peersRef.current[peerUserId]?.mark?.(`ERROR ${err?.name || ""} ${String(err?.message || err).slice(0, 90)}`);
    });
    chainsRef.current[peerUserId] = next;
    return next;
  }, []);

  // ---- Peer lifecycle ----
  const teardownPeer = useCallback((peerUserId) => {
    const peer = peersRef.current[peerUserId];
    if (!peer) return;
    try { peer.source?.disconnect(); } catch { /* ignore */ }
    try { peer.pc?.close(); } catch { /* ignore */ }
    if (peer.remoteStream) {
      try { peer.remoteStream.getTracks().forEach((tr) => tr.stop()); } catch { /* ignore */ }
    }
    if (peer.audioEl) {
      try { peer.audioEl.srcObject = null; peer.audioEl.remove(); } catch { /* ignore */ }
    }
    delete peersRef.current[peerUserId];
    // Nothing still queued for that connection is worth sending.
    outboxRef.current = outboxRef.current.filter((e) => e.msg.target_user_id !== peerUserId);
  }, []);

  const createPeer = useCallback((peerUserId, cid) => {
    const pc = new RTCPeerConnection({ iceServers: iceRef.current.servers });

    const audioEl = document.createElement("audio");
    audioEl.autoplay    = true;
    audioEl.playsInline = true;
    audioEl.setAttribute("playsinline", "");
    audioEl.controls = false;
    audioEl.volume   = 1.0;
    // "Muted autoplay" trick: browsers always allow muted autoplay.
    // We start muted so play() succeeds immediately (unlocking the element),
    // then unmute once a track arrives. This keeps the HTMLMediaElement as the
    // audio output path so the browser's AEC retains its reference signal.
    audioEl.muted = true;
    document.body.appendChild(audioEl);

    // Persistent MediaStream: tracks are swapped here; srcObject never changes.
    const remoteStream = new MediaStream();
    audioEl.srcObject  = remoteStream;
    audioEl.play().catch(() => {}); // unlock element while muted (safe, no gesture needed)

    const now = Date.now();
    const peer = {
      pc,
      cid,
      audioEl,
      remoteStream,
      sender:    null,
      iceBuffer: [],
      createdAt: now,
      badSince:  now,
      deadline:  now + patienceAfter(attemptsRef.current[peerUserId] || 0),
      restartAt: 0,
      talkUntil: 0,
    };
    peersRef.current[peerUserId] = peer;

    // What this connection goes through, for the diagnostic report.
    const diag = { role: selfUserId < peerUserId ? "caller" : "callee", cid: String(cid).slice(0, 6), at: now, events: [], local: {}, remote: {}, addFailed: 0, iceErrors: 0, pairs: "" };
    peer.diag = diag;
    peer.mark = (label) => {
      if (diag.events.length < 40) diag.events.push(`+${((Date.now() - now) / 1000).toFixed(1)} ${label}`);
    };
    diagRef.current[peerUserId] = [...(diagRef.current[peerUserId] || []).slice(-2), diag];
    pc.oniceconnectionstatechange = () => peer.mark(`ice ${pc.iceConnectionState}`);
    pc.onicegatheringstatechange = () => peer.mark(`gathering ${pc.iceGatheringState}`);
    pc.onicecandidateerror = (ev) => {
      diag.iceErrors += 1;
      diag.lastIceError = `${ev.errorCode} ${ev.url || ""}`;
    };

    pc.ontrack = (ev) => {
      const newTrack = ev.track;
      // Skip if this exact track is already in the stream — ICE restarts can
      // re-fire ontrack with the same track object, causing doubled audio.
      if (remoteStream.getTracks().some((tr) => tr.id === newTrack.id)) return;
      remoteStream.getTracks().forEach((tr) => remoteStream.removeTrack(tr));
      remoteStream.addTrack(newTrack);
      audioEl.muted = inputMutedRef.current;
      if (!inputMutedRef.current) {
        audioEl.play().catch(() => {
          window.addEventListener("pointerdown", () => {
            audioEl.muted = inputMutedRef.current;
            if (!inputMutedRef.current) audioEl.play().catch(() => {});
          }, { once: true });
        });
      }
      attachAnalyzer(peerUserId, remoteStream);
    };

    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return;
      const kind = kindOf(ev.candidate);
      diag.local[kind] = (diag.local[kind] || 0) + 1;
      sendSignal(peerUserId, "voice_ice", ev.candidate.toJSON(), cid);
    };

    return peer;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendSignal, selfUserId]); // attachAnalyzer only reads refs

  // Caller only. `iceRestart` renegotiates the same connection on new paths.
  const sendOffer = useCallback(async (peerUserId, peer, iceRestart = false) => {
    if (peersRef.current[peerUserId] !== peer) return;       // replaced meanwhile
    if (peer.pc.signalingState !== "stable") return;         // an offer is already out
    const offer = await peer.pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
    await peer.pc.setLocalDescription(offer);
    sendSignal(peerUserId, "voice_offer", peer.pc.localDescription, peer.cid);
    peer.mark(iceRestart ? "offer out (ice restart)" : "offer out");
  }, [sendSignal]);

  // Caller only: (re)build the connection to this player from scratch.
  const startCall = useCallback((peerUserId) => {
    teardownPeer(peerUserId);
    const peer = createPeer(peerUserId, newCallId());
    try {
      // sendrecv from the first offer, with or without a mic: the track is
      // attached later with replaceTrack, which never renegotiates.
      const tr = peer.pc.addTransceiver("audio", { direction: "sendrecv" });
      peer.sender = tr.sender;
      preferOpus(tr);
      const track = trackOf(localStreamRef);
      if (track) tr.sender.replaceTrack(track).catch(() => {});
    } catch { /* ignore */ }
    enqueue(peerUserId, () => sendOffer(peerUserId, peer));
  }, [teardownPeer, createPeer, enqueue, sendOffer]);

  // ---- Inbound signaling ----
  const handleSignal = useCallback(async (fromId, msg) => {
    const now    = Date.now();
    const caller = isCaller(fromId);
    let peer     = peersRef.current[fromId];

    if (msg.type === "voice_bye") {
      if (peer && (!msg.cid || msg.cid === peer.cid)) teardownPeer(fromId);
      return;
    }

    if (msg.type === "voice_hello") {
      // The callee wants a link, or reports that ours stopped working.
      if (!caller) return;
      if (peer && msg.cid === peer.cid) {
        if (msg.data?.restart && now - peer.restartAt > ICE_RESTART_EVERY) {
          peer.restartAt = now;
          await sendOffer(fromId, peer, true);
        }
        return;
      }
      // It knows nothing of my current connection. If I only just built it,
      // this hello merely crossed my offer.
      if (peer && now - peer.createdAt < FRESH_MS) return;
      startCall(fromId);
      return;
    }

    if (msg.type === "voice_offer") {
      if (caller || !msg.cid || !msg.data) return;
      if (!peer || peer.cid !== msg.cid) {
        // The caller gave up on a connection that was fully negotiated here:
        // the descriptions got through, the network path did not.
        if (peer && !isLinked(peer.pc) && peer.pc.remoteDescription && now - peer.createdAt > FRESH_MS) {
          whyRef.current[fromId] = "network";
        }
        teardownPeer(fromId);
        peer = createPeer(fromId, msg.cid);
      }
      const pc = peer.pc;
      peer.mark("offer in");
      await pc.setRemoteDescription(msg.data);
      await flushCandidates(peer);
      if (!peer.sender) {
        // Send on the m-line the caller offered: one bidirectional audio line.
        const tr = pc.getTransceivers()[0];
        if (tr) {
          tr.direction = "sendrecv";
          peer.sender  = tr.sender;
          const track  = trackOf(localStreamRef);
          if (track) await tr.sender.replaceTrack(track);
        }
      }
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendSignal(fromId, "voice_answer", pc.localDescription, peer.cid);
      peer.mark("answer out");
      return;
    }

    if (msg.type === "voice_answer") {
      if (!caller || !peer || peer.cid !== msg.cid || !msg.data) return;
      if (peer.pc.signalingState !== "have-local-offer") return;
      peer.mark("answer in");
      await peer.pc.setRemoteDescription(msg.data);
      await flushCandidates(peer);
      return;
    }

    if (msg.type === "voice_ice") {
      if (!peer || peer.cid !== msg.cid || !msg.data) return;
      const kind = kindOf(msg.data);
      peer.diag.remote[kind] = (peer.diag.remote[kind] || 0) + 1;
      if (!peer.pc.remoteDescription) peer.iceBuffer.push(msg.data);
      else {
        try { await peer.pc.addIceCandidate(msg.data); } catch { peer.diag.addFailed += 1; }
      }
    }
  }, [isCaller, teardownPeer, createPeer, startCall, sendOffer, sendSignal]);

  // ---- Watchdog: build missing links, heal or rebuild broken ones ----
  const tick = useCallback(() => {
    const now    = Date.now();
    const online = wsOpen();
    const next   = {};

    const known = new Set(playersRef.current.map((p) => p.user_id));
    for (const uid of Object.keys(peersRef.current)) {
      if (!known.has(uid)) teardownPeer(uid);
    }

    for (const p of playersRef.current) {
      const uid       = p.user_id;
      const caller    = isCaller(uid);
      // Signaling only reaches a player whose game WebSocket is up.
      const reachable = online && p.is_connected !== false && !p.has_quit;
      let peer        = peersRef.current[uid];

      if (peer && isLinked(peer.pc)) {
        peer.badSince = 0;
        peer.deadline = 0;
        attemptsRef.current[uid] = 0;
        warnedRef.current[uid]   = null;
        whyRef.current[uid]      = null;
        if (!peer.traced) {
          peer.traced = true;
          const name = p.name;
          routeOf(peer.pc).then((route) => trace(`link with ${name}: connected (${route})`));
        }
        if (peer.audioEl.srcObject && peer.audioEl.paused && !inputMutedRef.current) {
          peer.audioEl.play().catch(() => {});
        }
      } else if (peer) {
        if (!peer.badSince) {
          peer.badSince = now;
          peer.deadline = now + patienceAfter(attemptsRef.current[uid] || 0);
        }
        const st = peer.pc.iceConnectionState;
        if (now - (peer.pairsAt || 0) > 2000) {
          peer.pairsAt = now;
          const d = peer.diag;
          pairSummary(peer.pc).then((text) => { d.pairs = text; });
        }
        if (st === "failed") whyRef.current[uid] = "network";
        const dropped = st === "failed" || (st === "disconnected" && now - peer.badSince > DISCONNECT_GRACE);
        if (dropped && reachable && now - peer.restartAt > ICE_RESTART_EVERY) {
          peer.restartAt = now;
          const current = peer;
          if (caller) enqueue(uid, () => sendOffer(uid, current, true));
          else sendSignal(uid, "voice_hello", { restart: true }, peer.cid);
        }
        if (reachable && now > peer.deadline) {
          attemptsRef.current[uid] = (attemptsRef.current[uid] || 0) + 1;
          // Descriptions were exchanged and still no path: the network. No
          // answer at all: the other app.
          whyRef.current[uid] = peer.pc.remoteDescription ? "network" : "silent";
          peer.mark(`gave up: ${whyRef.current[uid]} (ice ${st}, signaling ${peer.pc.signalingState}, gathering ${peer.pc.iceGatheringState})`);
          if (caller && micActiveRef.current) {
            startCall(uid);
            peer = peersRef.current[uid];
          } else {
            // Callee, or a caller with nothing to send: drop the dead link.
            // Whoever has a mic on asks for a new one below / on its side.
            teardownPeer(uid);
            peer = null;
            helloAtRef.current[uid] = 0;
          }
        }
      }

      if (!peer && micActiveRef.current && reachable) {
        if (caller) {
          startCall(uid);
          peer = peersRef.current[uid];
        } else if (now - (helloAtRef.current[uid] || 0) > HELLO_EVERY) {
          helloAtRef.current[uid] = now;
          sendSignal(uid, "voice_hello", null, null);
        }
      }

      // What the UI shows for this player. An offline player has no state
      // here: the board already marks them as disconnected.
      const wanted = micActiveRef.current && reachable;
      if (peer || !wanted) waitingRef.current[uid] = 0;
      else if (!waitingRef.current[uid]) waitingRef.current[uid] = now;
      // A callee whose hellos get no offer back.
      if (waitingRef.current[uid] && now - waitingRef.current[uid] > LINK_PATIENCE) whyRef.current[uid] = "silent";
      const why = now - (outdatedRef.current[uid] || 0) < OUTDATED_TTL ? "outdated" : whyRef.current[uid];

      if (peer && isLinked(peer.pc)) next[uid] = "connected";
      else if ((peer && reachable) || wanted) next[uid] = why || "connecting";

      if (VOICE_FAIL_KEY[next[uid]] && warnedRef.current[uid] !== next[uid]) {
        warnedRef.current[uid] = next[uid];
        trace(`no link with ${p.name}: ${next[uid]}${next[uid] === "network" && !iceRef.current.turn ? " (no TURN relay configured)" : ""}`);
        toast.error(t(VOICE_FAIL_KEY[next[uid]], { name: p.name }), {
          duration: 12000,
          action: { label: t("voice.details"), onClick: () => setReport(buildReport()) },
        });
      }
    }

    setLinks((prev) => (sameMap(prev, next) ? prev : next));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsOpen, isCaller, teardownPeer, startCall, sendOffer, sendSignal, enqueue]); // buildReport only reads refs

  // Plain text for whoever has to find out why a link fails: what each
  // connection gathered, received and tried. Technical on purpose, not translated.
  function buildReport() {
    const lines = [
      `Rami voice report · ${new Date().toISOString()}`,
      `UA: ${navigator.userAgent}`,
      `ws ${wsOpen() ? "open" : "CLOSED"} · mic ${micActiveRef.current ? "on" : "off"} · listening ${inputMutedRef.current ? "off" : "on"} · TURN ${iceRef.current.turn ? "configured" : "none"} (${iceRef.current.from}) · online ${navigator.onLine}`,
    ];
    for (const p of playersRef.current) {
      const uid = p.user_id;
      const live = peersRef.current[uid];
      lines.push("", `[${p.name}] ${live ? (isLinked(live.pc) ? "CONNECTED" : `not connected (${whyRef.current[uid] || "trying"})`) : "no connection"} · server says ${p.is_connected === false ? "offline" : "online"} · rebuilt ${attemptsRef.current[uid] || 0}×`);
      for (const d of diagRef.current[uid] || []) {
        lines.push(
          `  ${d.role} · call ${d.cid} · ${((Date.now() - d.at) / 1000).toFixed(0)}s ago`,
          `    local candidates: ${tally(d.local)} · remote: ${tally(d.remote)} (rejected ${d.addFailed})`,
          `    pairs: ${d.pairs || "not sampled"}${d.iceErrors ? ` · ice errors ${d.iceErrors} (last ${d.lastIceError})` : ""}`,
          `    ${d.events.join(" · ") || "no event"}`,
        );
      }
    }
    return lines.join("\n");
  }

  useEffect(() => {
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [tick]);

  // Something changed that may let a stuck link through: look at it again soon
  // instead of waiting for its full deadline.
  const resyncSoon = useCallback(() => {
    const now  = Date.now();
    const soon = now + RESYNC_SOON;
    for (const peer of Object.values(peersRef.current)) {
      // A link that was only just built lost nothing: it keeps its full deadline.
      if (isLinked(peer.pc) || now - peer.createdAt < FRESH_MS) continue;
      peer.deadline = Math.min(peer.deadline || soon, soon);
    }
    helloAtRef.current = {};
    tick();
  }, [tick]);

  // wsVersion increments each time Game.jsx creates a new WebSocket, forcing
  // this effect to re-run and attach to the fresh ws object.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;

    const onMessage = (ev) => {
      let msg;
      try {
        if (typeof ev.data !== "string") return;
        if (ev.data === "pong") return;
        msg = JSON.parse(ev.data);
      } catch { return; }
      if (!msg?.type?.startsWith?.("voice_")) return;
      const fromId = msg.from_user_id;
      if (!fromId || fromId === selfUserId) return;
      // Security: only accept signals from real (non-bot) players in this room.
      if (!playersRef.current.some((p) => p.user_id === fromId)) return;
      // Security: reject oversized payloads (SDP ≈ 3 KB, ICE ≈ 200 B).
      if (msg.data != null && JSON.stringify(msg.data).length > 16_384) return;
      // Every message of this protocol has a `cid` field (possibly null). One
      // without it comes from a page still running the previous voice chat,
      // which cannot talk to this one.
      if (!("cid" in msg)) { outdatedRef.current[fromId] = Date.now(); return; }
      enqueue(fromId, () => handleSignal(fromId, msg));
    };

    const onOpen = () => { flushOutbox(); resyncSoon(); };

    ws.addEventListener("message", onMessage);
    ws.addEventListener("open", onOpen);
    if (ws.readyState === 1) onOpen();
    return () => {
      ws.removeEventListener("message", onMessage);
      ws.removeEventListener("open", onOpen);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsVersion, selfUserId, enqueue, handleSignal, flushOutbox, resyncSoon]);

  // A player joined, left, or its WebSocket came back; or my mic changed.
  const reachableKey = otherPlayers
    .map((p) => `${p.user_id}:${p.is_connected !== false && !p.has_quit ? 1 : 0}`)
    .join(",");
  useEffect(() => {
    resyncSoon();
  }, [micActive, reachableKey, resyncSoon]);

  // ---- Sync inputMuted prop → all peer audioEl ----
  useEffect(() => {
    Object.values(peersRef.current).forEach((peer) => {
      if (!peer?.audioEl) return;
      peer.audioEl.muted = inputMuted;
      if (!inputMuted) peer.audioEl.play().catch(() => {});
    });
    if (!inputMuted) resumeAudioCtx();
  }, [inputMuted, resumeAudioCtx]);

  // ---- Notify parent ----
  useEffect(() => {
    onSpeakingChange?.(speaking);
  }, [speaking, onSpeakingChange]);

  useEffect(() => {
    onLinksChange?.(links);
  }, [links, onLinksChange]);

  // ---- visibilitychange: resume AudioContext and retry audio on tab restore ----
  useEffect(() => {
    const onVisible = () => {
      resumeAudioCtx();
      if (!inputMutedRef.current) {
        Object.values(peersRef.current).forEach((peer) => {
          if (peer?.audioEl?.srcObject && peer.audioEl.paused) {
            peer.audioEl.play().catch(() => {});
          }
        });
      }
    };
    const handleVisibility = () => { if (!document.hidden) onVisible(); };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [resumeAudioCtx]);

  // ---- Microphone ----
  const releaseStream = () => {
    const stream = localStreamRef.current;
    localStreamRef.current = null;
    if (!stream) return;
    stream.getTracks().forEach((tr) => {
      tr.onended = null; tr.onmute = null; tr.onunmute = null;
      tr.stop();
    });
  };

  // Use this stream as the outgoing mic on every link.
  const adoptStream = async (stream) => {
    releaseStream();
    localStreamRef.current = stream;
    const track = stream.getAudioTracks()[0];
    if (track) {
      track.enabled = true;
      // The system took the mic away (call, headset unplugged, permission
      // revoked): try once to get it back, otherwise show the mic as off.
      track.onended = async () => {
        if (!micActiveRef.current || localStreamRef.current !== stream) return;
        try {
          await adoptStream(await requestMicStream());
          setMicHeld(false);
        } catch {
          stopMic();
          toast.error(t("voice.lost"));
        }
      };
      track.onmute   = () => setMicHeld(true);
      track.onunmute = () => setMicHeld(false);
    }
    attachSelfAnalyser(stream);
    for (const peer of Object.values(peersRef.current)) {
      if (!peer.sender) continue;
      try { await peer.sender.replaceTrack(track || null); } catch { /* ignore */ }
    }
  };

  // Does NOT affect what we hear.
  const stopMic = () => {
    setMicActive(false);
    micActiveRef.current = false;
    setMicHeld(false);
    detachSelfAnalyser();
    Object.values(peersRef.current).forEach((peer) => {
      // replaceTrack(null) stops sending; the line stays sendrecv so we keep receiving.
      try { peer.sender?.replaceTrack(null); } catch { /* ignore */ }
    });
    releaseStream();
  };

  const activateMic = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error(t("voice.unsupported"));
      return;
    }
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      toast.error(t("voice.insecure"));
      return;
    }
    try {
      const stream = await requestMicStream();
      // Short-lived relay credentials may have expired since the page loaded.
      if (Date.now() - iceRef.current.at > ICE_REFRESH_MS) await loadIce();
      setMicActive(true);
      micActiveRef.current = true;

      const ctx = ensureAudioCtx();
      if (ctx?.state === "suspended") {
        try { await ctx.resume(); } catch { /* ignore */ }
      }
      await adoptStream(stream);

      // This click is a user gesture: unlock playback on every element (iOS).
      Object.values(peersRef.current).forEach((peer) => {
        peer.audioEl.play().catch(() => {});
      });
      trace(`mic on · TURN relay: ${iceRef.current.turn ? "configured" : "none (direct connections only)"}`);
      // Talking implies listening.
      onMicOn?.();
      resyncSoon();
      toast.success(t("voice.on"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[VoiceChat] getUserMedia failed:", e);
      const name = e?.name || "";
      const msg =
        name === "NotAllowedError" || name === "PermissionDeniedError"
          ? t("voice.denied")
          : name === "NotFoundError"
          ? t("voice.notFound")
          : name === "NotReadableError"
          ? t("voice.busy")
          : t("voice.inaccessible", { reason: e?.message || name || t("voice.unknown") });
      toast.error(msg);
    }
  };

  const deactivateMic = () => {
    stopMic();
    toast.info(t("voice.off"));
  };

  // ---- Cleanup on unmount ----
  useEffect(() => () => {
    Object.entries(peersRef.current).forEach(([uid, peer]) => {
      try { sendSignal(uid, "voice_bye", null, peer.cid); } catch { /* ignore */ }
    });
    releaseStream();
    try { selfSourceRef.current?.disconnect(); } catch { /* ignore */ }
    Object.keys(peersRef.current).forEach((uid) => teardownPeer(uid));
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch { /* ignore */ } }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teardownPeer, sendSignal]);

  // ---- UI ----
  return (
    <div className="flex items-center">
      <Button
        size="sm"
        variant="ghost"
        onClick={micActive ? deactivateMic : activateMic}
        className={`h-8 px-2 ${
          micActive
            ? micHeld ? "text-rose-400" : selfSpeaking ? "text-emerald-300" : "text-amber-300"
            : "text-slate-400 hover:text-slate-200"
        }`}
        title={micActive ? (micHeld ? t("voice.held") : t("voice.disable")) : t("voice.enable")}
        data-testid="voice-mic-btn"
      >
        {micActive && !micHeld
          ? selfSpeaking
            ? <Mic className="h-4 w-4 animate-pulse" />
            : <Mic className="h-4 w-4" />
          : <MicOff className="h-4 w-4" />
        }
      </Button>
      {Object.values(links).some((state) => VOICE_FAIL_KEY[state]) && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setReport(buildReport())}
          className="h-8 px-1.5 text-rose-400"
          title={t("voice.reportTitle")}
          aria-label={t("voice.reportTitle")}
          data-testid="voice-report-btn"
        >
          <Stethoscope className="h-4 w-4" />
        </Button>
      )}
      <Dialog open={report != null} onOpenChange={(open) => { if (!open) setReport(null); }}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("voice.reportTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400">{t("voice.reportHint")}</p>
          <pre
            dir="ltr"
            data-testid="voice-report"
            className="max-h-[45vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-black/40 p-3 text-left text-[10px] leading-relaxed text-slate-300"
          >
            {report}
          </pre>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="text-slate-300" onClick={() => setReport(buildReport())}>
              {t("voice.refresh")}
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              data-testid="voice-report-copy"
              onClick={async () => {
                try { await navigator.clipboard.writeText(report || ""); toast.success(t("voice.copied")); }
                catch { toast.error(t("common.error")); }
              }}
            >
              {t("voice.copy")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoiceChat;
